"use client";

/**
 * The pending-ask lock on a task's composer, in one place for the layout's box
 * and the Dock's pill. Moved out of task/layout.tsx verbatim.
 *
 * R16: the ask that gates the composer is the one on the SCREEN it sits under.
 * The lead's column is hidden while a lane is up, so a permission card waiting
 * on Bash must not shut the box under a helper's screen. Auth asks never shut
 * it (the person connects elsewhere, then sends). A pending QUESTION never
 * shuts it either: the backend supersedes the ask on a new message by design,
 * and locking send behind the card wedged users mid-flow (2026-08-25).
 */
import { useTaskStore } from "@/stores/taskStore";
import {
  useSelectedTaskPendingInteraction,
  useTaskPendingInteraction,
} from "@/stores/selectors";
import { useLaneInView } from "@/stores/teamViewStore";
import type { PendingInteraction } from "@/stores/types";

export interface TaskComposerGate {
  /** The pending ask on the screen the composer sits under, blocking or not. */
  pending: PendingInteraction | null;
  /** The one that shuts the composer: never an auth ask, never a question. */
  blocking: PendingInteraction | null;
  /** What the shut composer says, or null while it is open. */
  lockedWords: string | null;
  /** The desk is on screen for this task. */
  showDesk: boolean;
}

/** The task layout's: the store's selection, read exactly as it always was. */
export function useTaskComposerGate(): TaskComposerGate {
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  return useGateOf(selectedTaskId, useSelectedTaskPendingInteraction());
}

/**
 * The Dock's pill's: the gate FOR a task it names, and null off a task's
 * screen, where the gate is open and reads nothing of a lingering selection
 * (review-task-13 finding 1; see useTaskScreenControlsFor for why two hooks).
 */
export function useTaskComposerGateFor(
  taskId: string | null
): TaskComposerGate {
  return useGateOf(taskId, useTaskPendingInteraction(taskId));
}

function useGateOf(
  selectedTaskId: string | null,
  leadPending: PendingInteraction | null
): TaskComposerGate {
  const laneInView = useLaneInView(selectedTaskId);
  const lanePending = useTaskPendingInteraction(laneInView);
  const pending = laneInView ? lanePending : leadPending;
  const blocking =
    pending && pending.type !== "auth" && pending.type !== "question"
      ? pending
      : null;
  const showDesk = !!selectedTaskId;
  const lockedWords = blocking
    ? showDesk
      ? "Answer above to continue"
      : "Respond to the question above to continue"
    : null;
  return { pending, blocking, lockedWords, showDesk };
}
